import { useState } from 'react';
import { unassignTopic } from '../repositories/adminStoryRepository';
import { ModalOverlay } from './ModalOverlay';

interface UnlinkTopicModalProps {
  /** Article(s) to clear the topic assignment from. */
  articleIds: string[];
  /** Human label for the article(s) being unlinked, shown in the prompt. */
  label: string;
  /** The topic the article(s) are being removed from, for the prompt. */
  topicName?: string | null;
  onClose: () => void;
  /** Called with the ids that were successfully unlinked. */
  onUnlinked: (ids: string[]) => void;
}

/**
 * Confirmation dialog for unlinking one or more articles from their topic.
 * Owns the in-flight state and the unassign-topic API call so callers only
 * declare *what* to unlink and react to *which* ids were unlinked.
 */
export function UnlinkTopicModal({
  articleIds,
  label,
  topicName,
  onClose,
  onUnlinked,
}: UnlinkTopicModalProps) {
  const [unlinking, setUnlinking] = useState(false);

  const handleConfirm = async () => {
    setUnlinking(true);
    try {
      await Promise.all(articleIds.map((id) => unassignTopic(id)));
      onUnlinked(articleIds);
    } catch {
      // admin can retry
    } finally {
      setUnlinking(false);
    }
  };

  return (
    <ModalOverlay onClose={onClose} className="score-feedback-modal">
      <div className="score-feedback-modal-title">Unlink from topic</div>
      <div style={{ fontSize: '0.82rem', color: 'var(--gray-500)', marginBottom: 8 }}>
        Remove <strong>{label}</strong>
        {topicName ? <> from topic &ldquo;{topicName}&rdquo;</> : null}?
      </div>
      <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)', marginBottom: 12 }}>
        This clears the topic assignment so the article(s) will no longer be grouped here.
      </div>
      <div className="score-feedback-modal-actions">
        <button className="primary-button" disabled={unlinking} onClick={handleConfirm}>
          {unlinking ? 'Unlinking...' : 'Confirm'}
        </button>
        <button className="secondary-button" disabled={unlinking} onClick={onClose}>
          Cancel
        </button>
      </div>
    </ModalOverlay>
  );
}
